import { useSession } from "@clerk/clerk-react";
import axios from "axios";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle,
  ExternalLink,
  Info,
  Loader2,
  TriangleAlert,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useCurrentConvo } from "@/store/zustand/aiActionStore";
import {
  type ManualAssistantMessage,
  useAiContextStore,
  useIsChatLoading,
} from "@/store/zustand/aiContextStore";
import { useDevServerStore } from "@/store/zustand/devServerStore";
import { useIssuesStore } from "@/store/zustand/issues-store";
import useLayoutStore, { LeftTab } from "@/store/zustand/layoutStore";
import { useProjectModalStore } from "@/store/zustand/projectModalStore";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import {
  useLaunchContainer,
  useRunTerminalCommand,
  useStartServer,
} from "@/utils/devServerUtils";
import { track } from "@/utils/events/tracker";
import { ReactFramework } from "@/utils/templateProjects";
import {
  getAxiosErorrMessage,
  getUnexpiredToken,
  logAxiosError,
  useActiveMainCanvas,
  useActiveProject,
  useCanvasId,
} from "@/utils/utils";
import { AddMembers } from "./AddMembers";
import { DevServer } from "./ConsoleV2/DevServer";
import { useDatabaseSyncWrapperStore } from "./contexts/databaseSyncStore";
import { Alert, AlertDescription } from "./ui/alert";

interface Props {
  closeModal: () => void;
}

interface VercelFileError {
  name: string;
  size: string;
}

const getDeployCommand = (canvasId: string, framework: ReactFramework) => {
  let basePath = `/${canvasId}`;
  if (import.meta.env.VITE_ENVIRONMENT === "DEV") {
    basePath = `/preview${basePath}`;
  }

  let command, envVariables;

  if (
    framework === ReactFramework.NEXT_JS ||
    framework === ReactFramework.NEXT_JS_APP_ROUTER
  ) {
    command = `npm run build && mv out dist`;
    envVariables = {
      NEXT_PUBLIC_OUTPUT_MODE: "export",
      NEXT_PUBLIC_BASE_PATH: basePath,
      NEXT_PUBLIC_IGNORE_ERRORS: "true",
      TEMPO: "false",
      NEXT_PUBLIC_TEMPO: "false",
    };
  } else if (framework === ReactFramework.VITE) {
    command = `npm run build`;
    envVariables = {
      VITE_BASE_PATH: basePath,
      VITE_IGNORE_ERRORS: "true",
      TEMPO: "false",
      VITE_TEMPO: "false",
    };
  }

  // TODO: Remix
  return {
    command,
    envVariables,
  };
};

const getPreviewUrlForCanvas = (canvasId: string) => {
  if (import.meta.env.VITE_ENVIRONMENT === "DEV") {
    return `http://localhost:3001/preview/${canvasId}`;
  }

  if (import.meta.env.VITE_ENVIRONMENT === "STAGING") {
    return `https://staging-preview.tempo-dev.app/${canvasId}`;
  }

  // Important - this points to the router, which then points to the correct machine
  return `https://view.tempo-dev.app/${canvasId}`;
};

const ShareModal: React.FC<Props> = (props) => {
  const [selectedDeployOption, setSelectedDeployOption] =
    useState<"vercel">("vercel");
  const setTempAssistantMessage = useAiContextStore(
    (state) => state.setTempAssistantMessage,
  );
  const setAddManualMessage = useAiContextStore(
    (state) => state.setAddManualMessage,
  );
  const addTerminalErrors = useDevServerStore(
    (state) => state.addTerminalErrors,
  );
  const terminalErrors = useDevServerStore((state) => state.terminalErrors);
  const { session } = useSession();

  const updateCanvas = useIssuesStore((state) => state.updateCanvas);

  const canvasId = useCanvasId();

  const canvas = useActiveMainCanvas();
  const canvasRef = useRef(canvas);
  useEffect(() => {
    canvasRef.current = canvas;
  }, [canvas]);

  const project = useActiveProject();

  const [deploying, setDeploying] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const runTerminalCommand = useRunTerminalCommand();

  const onDbSnapshot = useSupabaseStore((state) => state.onDbSnapshot);

  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);

  const [latestVercelDeployment, setLatestVercelDeployment] = useState(null);
  const { conversation, messages } = useCurrentConvo();
  const selectedConversationId = conversation?.id;

  const isChatLoading = useIsChatLoading();

  const setNewLayout = useLayoutStore((store) => store.setNewLayout);

  const launchContainer = useLaunchContainer();
  const startServer = useStartServer();

  const [isUpdatingProject, setIsUpdatingProject] = useState(false);

  useEffect(() => {
    const fetchDeployments = async () => {
      if (!supabaseClient) return;

      const { data, error } = await supabaseClient
        .from("vercel_deployments")
        .select("*")
        .eq("canvas_id", canvasId)
        .order("created_at", { ascending: false })
        .limit(1);

      if (error) {
        console.error(error);
      } else {
        setLatestVercelDeployment(data[0]);
      }
    };

    fetchDeployments();
  }, [canvasId, supabaseClient]);

  const deployWithVercel = async () => {
    if (isChatLoading || !supabaseClient) return;

    // open ai chat to show deployment status & errors
    setNewLayout(LeftTab.AIChat);

    // clear errors before rebuilding
    useDevServerStore.getState().clearAllErrors();

    const msg: ManualAssistantMessage = {
      id: uuidv4().toString(),
      text: "Preparing project for deployment with Vercel...",
      progressItems: [
        { title: "Building project", loading: true },
        { title: "Deploying to Vercel" },
      ],
    };
    setTempAssistantMessage({ ...msg });
    const { success, error } = await runTerminalCommand({
      command: "npm run build",
    });

    if (!success) {
      // @ts-expect-error TS18048
      msg.progressItems.forEach((item) => (item["failed"] = true));
      msg.afterText = "Project failed to deploy to Vercel. Please try again.";
      setTempAssistantMessage(null);
      setAddManualMessage({ ...msg });
      toast.error("Build failed. Please try again.");
      return;
    }

    if (terminalErrors.length > 0) {
      // @ts-expect-error TS18048
      msg.progressItems.forEach((item) => (item["failed"] = true));
      msg.afterText =
        "Project failed to build with errors. Please fix the errors and try again.";
      setTempAssistantMessage(null);
      setAddManualMessage({ ...msg });
      toast.error(
        "Build failed with errors. Please fix the errors and try again.",
      );
      return;
    }

    // Success case
    // @ts-expect-error TS18048
    msg.progressItems[0]["succeeded"] = true;
    // @ts-expect-error TS18048
    msg.progressItems[1]["loading"] = true;
    setTempAssistantMessage({ ...msg });

    if (!project?.id) {
      // deploy failed
      // @ts-expect-error TS18048
      msg.progressItems[1]["failed"] = true;
      msg.afterText = "Project failed to deploy to Vercel";
      setAddManualMessage({ ...msg });
      setTempAssistantMessage(null);
      return;
    }

    const supabaseJWT = await getUnexpiredToken(session);

    const response = await axios.post(
      `${
        import.meta.env.VITE_BACKEND_BASE_URL
      }/canvases/${canvasId}/deploy/vercel`,
      {
        // canvasId: canvasId,
        projectId: project.id,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${supabaseJWT}`,
        },
        validateStatus: (status) => true,
      },
    );

    setTempAssistantMessage(null);
    if (response.status === 200) {
      const addMessageToDb = async () => {
        const fullTextContent =
          '\n<vercel_deployment_message>\n{"items":' +
          JSON.stringify({
            previewUrl: response.data.previewUrl,
            claimUrl: response.data.claimUrl,
          }) +
          "}\n</vercel_deployment_message>";

        const newMessage: any = {
          id: uuidv4().toString(),
          conversation_id: selectedConversationId,
          canvas_id: canvasId,
          content: fullTextContent,
          role: "assistant",
          message_context: null,
          tool_calls: null,
          tool_call_id: null,
          created_at: new Date().toISOString(),
        };

        onDbSnapshot("aiChatMessages", {
          newItems: [newMessage],
        });

        await supabaseClient.from("ai_chat_messages").insert(newMessage);
      };

      addMessageToDb();
    } else {
      // deploy failed
      // @ts-expect-error TS18048
      msg.progressItems[1]["failed"] = true;
      const vercelStringifiedError = JSON.stringify(
        response.data?.error?.message ||
          response.data?.error ||
          response.data ||
          "Unknown error occurred while deploying to Vercel: " +
            response.status,
      );

      const vercelLogs =
        (response.data?.logs as { text: string; type: string }[]) || [];

      const vercelErrorLogsString = vercelLogs
        .filter(
          (log) =>
            log.type === "fatal" ||
            log.type === "stderr" ||
            log.type === "exit",
        )
        .map((log) => log.text)
        .join("\n");

      if (vercelErrorLogsString) {
        addTerminalErrors([
          {
            message: vercelErrorLogsString,
            filename: "",
            stack: "",
            count: 1,
          },
        ]);
      }

      const vercelFileErrors: VercelFileError[] =
        response?.data?.details?.files || [];

      msg.afterText =
        vercelFileErrors.length > 0
          ? `Project failed to deploy to Vercel. ${vercelFileErrors.length} file(s) had issues. Error: ${vercelStringifiedError}.`
          : `Project failed to deploy to Vercel. Error: ${vercelStringifiedError}.`;

      if (vercelFileErrors && vercelFileErrors.length > 0) {
        const terminalErrors = vercelFileErrors.map(
          (fileError: VercelFileError) => ({
            message: vercelStringifiedError,
            filename: `${fileError.name} (${fileError.size})`,
            stack: "",
            count: 1,
          }),
        );
        addTerminalErrors(terminalErrors);
      } else {
        addTerminalErrors([
          {
            message: vercelStringifiedError,
            filename: "",
            stack: "",
            count: 1,
          },
        ]);
      }
      setAddManualMessage({ ...msg });

      toast.error(
        "Project failed to deploy to Vercel. Please see the AI Chat for errors.",
      );
    }

    return;
  };

  const upgradeNodeVersion = async () => {
    track("EDIT_PROJECT", { projectId: project?.id });
    setIsUpdatingProject(true);
    try {
      const urlToHit = `${import.meta.env.VITE_BACKEND_BASE_URL}/projects/${
        project?.id
      }/edit`;
      const supabaseJWT = await session?.getToken();

      await axios.put(
        urlToHit,
        {
          nodeVersion: "20",
        },
        {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
        },
      );

      useDatabaseSyncWrapperStore.getState().triggerRefetchProjects();
    } catch (err) {
      logAxiosError("Error editing project", err);
      toast.error(
        `Could not edit project, check if path is valid. Error: ${getAxiosErorrMessage(
          err,
        )}`,
      );
      return;
    } finally {
      setIsUpdatingProject(false);
    }
    props.closeModal();
    toast.success("Upgraded project to Node 20");

    const { success, error } = await launchContainer({
      forceRelaunch: true,
    });
    if (success) {
      startServer({
        forceRestart: true,
      });
    }
  };

  const needsUpgradeForVercel =
    Number.parseFloat(project?.nodeVersion ?? "20") < 20 &&
    Date.now() > Date.parse("2025-09-01T00:00:00Z");

  let isDeployDisabled;
  if (selectedDeployOption === "vercel") {
    isDeployDisabled = isChatLoading || needsUpgradeForVercel;
  } else {
    isDeployDisabled =
      deploying ||
      (project?.framework !== ReactFramework.NEXT_JS &&
        project?.framework !== ReactFramework.VITE &&
        project?.framework !== ReactFramework.NEXT_JS_APP_ROUTER);
  }

  // Register with project modal store
  useEffect(() => {
    useProjectModalStore.getState().addProjectModal("share-modal");
    return () => {
      useProjectModalStore.getState().removeProjectModal("share-modal");
    };
  }, []);

  return (
    <Dialog
      open={true}
      onOpenChange={() => {
        props.closeModal();
      }}
    >
      <DialogContent className="bg-secondary-0 text-white p-0 max-w-[400px] rounded-lg">
        <div className="divide-y divide-secondary-n-950 flex flex-col py-4 max-w-[400px]">
          <DialogHeader className="px-6 py-2 pt-0">
            <DialogTitle className="text-base text-white text-left font-medium">
              Share
            </DialogTitle>
            <DialogDescription></DialogDescription>
          </DialogHeader>

          <div
            className={cn(
              "px-6 py-4",
              project?.framework === ReactFramework.EXPO && "hidden",
            )}
          >
            <div className="flex flex-col w-full">
              <div className="text-sm font-medium text-white mb-3">
                Publish your project
              </div>
              <button
                disabled={
                  isDeployDisabled ||
                  (isChatLoading && selectedDeployOption === "vercel")
                }
                className={cn(
                  "flex items-center justify-between w-full bg-transparent border border-secondary-n-900 rounded-lg p-3 cursor-pointer transition-all text-left text-white hover:bg-secondary-n-950 hover:border-secondary-800 active:scale-[0.995] group",
                  (isDeployDisabled ||
                    (isChatLoading && selectedDeployOption === "vercel")) &&
                    "opacity-50 cursor-not-allowed",
                )}
                onClick={async () => {
                  if (isChatLoading && selectedDeployOption === "vercel")
                    return;
                  if (!project || !canvasId) return;

                  props.closeModal();
                  track("DEPLOY_APP", {
                    framework: project?.framework,
                    deployOption: "vercel",
                  });
                  deployWithVercel();
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-black border border-secondary-n-900 rounded-md flex items-center justify-center">
                    <img
                      src="/images/vercel-logo.svg"
                      className="w-4 h-4"
                      alt="Vercel"
                    />
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-sm font-semibold">
                      Deploy to Vercel
                    </span>
                    <span className="text-xs text-secondary-800">
                      1-Click deploy to production
                    </span>
                  </div>
                </div>
                {deploying ? (
                  <Loader2 className="h-4 w-4 animate-spin text-secondary-800" />
                ) : (
                  <ArrowRight className="h-5 w-5 text-secondary-800 transition-all group-hover:text-white group-hover:translate-x-0.5" />
                )}
              </button>

              {needsUpgradeForVercel && selectedDeployOption === "vercel" && (
                <Alert variant="destructive" className="mt-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    To deploy to Vercel, your project must use Node 20 or newer.
                    <Button
                      size="sm"
                      className="block mt-2"
                      variant="secondary"
                      onClick={upgradeNodeVersion}
                      isLoading={isUpdatingProject}
                      disabled={isUpdatingProject}
                    >
                      Upgrade to Node 20
                    </Button>
                  </AlertDescription>
                </Alert>
              )}

              {latestVercelDeployment && selectedDeployOption === "vercel" && (
                <div className="flex flex-col gap-4">
                  <div className="flex flex-row justify-between items-center space-x-2 mt-4 bg-secondary-n-900 p-1.5 rounded-md">
                    <div className="bg-secondary-n-950 text-white border rounded-md py-1 px-2 overflow-hidden flex flex-row items-center gap-2">
                      <p
                        className="truncate text-xs text-gray-100 font-['Inconsolata'] cursor-pointer"
                        onClick={() =>
                          window.open(
                            // @ts-expect-error TS2339
                            `https://${latestVercelDeployment.deployment_url}`,
                            "_blank",
                          )
                        }
                      >
                        {/*
                         // @ts-expect-error TS2339 */}
                        https://{latestVercelDeployment.deployment_url}
                      </p>
                      <CheckCircle className="w-3 h-3 text-confirm-500 shrink-0" />
                    </div>
                    <div className="flex pr-1">
                      <ExternalLink
                        className="cursor-pointer w-4 h-4 text-secondary-n-600 shrink-0"
                        onClick={() =>
                          window.open(
                            // @ts-expect-error TS2339
                            `https://${latestVercelDeployment.deployment_url}`,
                            "_blank",
                          )
                        }
                      />
                    </div>
                  </div>
                  <div className="rounded-md border border-secondary-n-900 p-2">
                    <div className="flex flex-row items-center gap-2">
                      {/*
                       // @ts-expect-error TS2339 */}
                      {latestVercelDeployment.claimed && (
                        <Info className="w-3.5 h-3.5 text-secondary-800 shrink-0 mb-auto" />
                      )}
                      <p className="text-xs text-gray-100">
                        {/*
                         // @ts-expect-error TS2339 */}
                        {latestVercelDeployment.claimed
                          ? "You've claimed your project! Manage or deploy to a custom domain in Vercel."
                          : `Your deployment is live! Claim it to manage in Vercel and deploy to a custom domain—link expires in 48 hours.\n`}
                      </p>
                    </div>
                    {/*
                     // @ts-expect-error TS2339 */}
                    {!latestVercelDeployment.claimed && (
                      <div className="flex flex-col gap-2 mt-2">
                        <span className="flex flex-row items-center gap-2">
                          <TriangleAlert className="w-4 h-4 text-[#E17100] shrink-0" />
                          <p className="text-xs text-secondary-800">
                            This link expires in 48 hours.
                          </p>
                        </span>
                        <Button
                          variant="default"
                          className="text-xs bg-primary-0 hover:bg-primary-100 whitespace-nowrap rounded-md h-8"
                          onClick={() =>
                            window.open(
                              // @ts-expect-error TS2339
                              `https://vercel.com/claim-deployment?code=${latestVercelDeployment.claim_code}`,
                              "_blank",
                            )
                          }
                        >
                          Claim Now
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* {canvas?.last_deployed_ts && selectedDeployOption === "tempo" && (
                <div className="flex flex-row justify-between items-center space-x-2 mt-4">
                  <p className="text-xs text-white font-medium whitespace-nowrap">
                    Tempo URL:{" "}
                  </p>
                 <div
                    onBeforeInput={() => {}}
                    className="bg-secondary-200 text-white border-secondary-500 text-xs border rounded-md p-2 overflow-x-scroll hide-scrollbar whitespace-nowrap flex-1"
                  >
                    {getPreviewUrlForCanvas(canvasId)}
                  </div>
                  <Button
                    variant="default"
                    className="text-xs bg-secondary-200 hover:bg-secondary-400 whitespace-nowrap"
                    onClick={() =>
                      copy(getPreviewUrlForCanvas(canvasId), setCopiedDeploy)
                    }
                    disabled={copiedDeploy}
                  >
                    {copiedDeploy ? "Copied!" : "Copy Link"}
                  </Button>
                </div>
              )} */}
            </div>
          </div>

          <div
            className={cn(
              !deploying && !showLogs && "hidden",
              "min-h-0 h-[50vh] p-4 pb-0",
            )}
          >
            <DevServer />
          </div>

          <div
            className={cn(
              "divide-y divide-secondary-n-950",
              (showLogs || deploying) && "hidden",
            )}
          >
            <AddMembers type="canvas" projectId={project?.id || ""} />
            {/* <div className="px-6 py-4">
              <div className="flex flex-col">
                <div className="text-sm font-bold text-white mb-2">
                  Share my canvas
                </div>
                <div className="text-xs text-secondary-900">
                  Share this link with other members who have access to the
                  project. Your canvas is unique to you—sharing it with others
                  will allow them to see your changes in real-time.
                </div>
                <div className="flex flex-row justify-between items-center space-x-2 mt-4">
                  <Input
                    onBeforeInput={() => {}}
                    className="bg-secondary-200 text-white border-secondary-500 text-xs py-0"
                    value={shareLink}
                  />
                  <Button
                    variant="default"
                    className="text-xs bg-secondary-200 hover:bg-secondary-400 whitespace-nowrap"
                    onClick={() => copy(shareLink, setCopied)}
                    disabled={copied}
                  >
                    {copied ? "Copied!" : "Copy Link"}
                  </Button>
                </div>
              </div>
            </div> */}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(ShareModal);
